import {
  DarkModeIconData,
  LightModeIconData,
  LogoutIconData,
} from "@meiden-connect/material-symbols";
import { BinarySwitch, Button, Icon, useThemeMode, useThemePreference } from "@meiden-connect/ui";
import { getWebConfig } from "../../env";
import { useAuthSession } from "../../auth/auth-session-context";
import { useCurrentActor } from "../../auth/use-current-actor";
import { saveDashboardThemePreference } from "../../theme/theme-preference";
import { UserAvatar } from "./user-avatar";

const MEIDEN_CONNECT_CATALOG_URL = "https://catalog.ours.connect.meidensha.co.jp/";

export function DashboardHeader(): JSX.Element {
  const themeMode = useThemeMode();
  const [, setThemePreference] = useThemePreference();
  const currentThemeLabel = themeMode === "dark" ? "ダークモード" : "ライトモード";

  const handleChangeTheme = (value: "ライトモード" | "ダークモード"): void => {
    const next = value === "ダークモード" ? "dark" : "light";
    setThemePreference(next);
    saveDashboardThemePreference(next);
  };

  return (
    <header className="dashboard-header">
      <a className="dashboard-logo" href="/">
        <img alt="" className="dashboard-logo-mark" src="/app-icon.png" />
        <span>リファインメントキャンバス</span>
      </a>
      <div className="dashboard-header-actions">
        <MeidenConnectLogo isLoggedIn={getWebConfig().authMode === "required"} themeMode={themeMode} />
        <BinarySwitch
          aria-label="表示テーマ"
          className="theme-toggle-switch"
          onChange={handleChangeTheme}
          options={[
            { d: LightModeIconData, value: "ライトモード" },
            { d: DarkModeIconData, value: "ダークモード" },
          ]}
          title={currentThemeLabel}
          value={currentThemeLabel}
        />
        {getWebConfig().authMode === "required" ? <RequiredUserChip /> : <MockUserChip />}
      </div>
    </header>
  );
}

function MeidenConnectLogo(props: {
  readonly isLoggedIn: boolean;
  readonly themeMode: "dark" | "light";
}): JSX.Element {
  const logoSrc =
    props.themeMode === "dark" ? "/meiden-connect-logo-dark.png" : "/meiden-connect-logo-light.png";

  return (
    <a
      aria-label="MEIDEN CONNECT ソリューションカタログ"
      className={`meiden-connect-logo ${props.isLoggedIn ? "" : "meiden-connect-logo-disabled"}`}
      href={props.isLoggedIn ? MEIDEN_CONNECT_CATALOG_URL : undefined}
      rel={props.isLoggedIn ? "noopener noreferrer" : undefined}
      target={props.isLoggedIn ? "_blank" : undefined}
      title={props.isLoggedIn ? "ソリューションカタログを開く" : "ログイン中のみ利用できます"}
    >
      <span className="meiden-connect-logo-powered">Powered by</span>
      <img alt="MEIDEN CONNECT" className="meiden-connect-logo-img" src={logoSrc} />
    </a>
  );
}

function RequiredUserChip(): JSX.Element {
  // SDK Provider が main tree から外れたため、ユーザー情報・サインアウト導線とも
  // AuthSessionContext (SessionSnapshot) を経由して取得する。
  const { user, signOut } = useAuthSession();
  const actor = useCurrentActor();
  const authServiceOrigin = getWebConfig().authServiceOrigin;
  const profileUrl =
    authServiceOrigin && user !== undefined
      ? `${authServiceOrigin}/users/${user.userId}/profile`
      : undefined;

  return (
    <div className="dashboard-user-chip" aria-label={`ログイン中: ${actor.displayName}`}>
      <a
        className="dashboard-user-identity-link"
        href={profileUrl}
        rel="noopener noreferrer"
        target="_blank"
        title="ユーザー情報を開く"
      >
        <UserAvatar
          alt="ログインユーザーのアイコン"
          displayName={actor.displayName}
          src={actor.avatarImageUrl}
        />
        <span className="dashboard-user-meta">
          <strong>{actor.displayName}</strong>
          <span>{user?.email ?? ""}</span>
        </span>
      </a>
      <Button
        aria-label="ログアウトする"
        className="dashboard-header-icon-button"
        onClick={() => void signOut()}
        title="ログアウトする"
        type="button"
        variant={undefined}
      >
        <Icon d={LogoutIconData} />
      </Button>
    </div>
  );
}

function MockUserChip(): JSX.Element {
  const actor = useCurrentActor();
  return (
    <div className="dashboard-user-chip" aria-label={`ローカル確認ユーザー: ${actor.displayName}`}>
      <UserAvatar
        alt="ローカル確認ユーザーのアイコン"
        displayName={actor.displayName}
        src={actor.avatarImageUrl}
      />
      <span className="dashboard-user-meta">
        <strong>{actor.displayName}</strong>
        <span>ローカル確認</span>
      </span>
    </div>
  );
}
